import axios from 'axios'
import { TOKEN_STORAGE_KEY, IMPERSONATE_STORAGE_KEY } from './constants'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
})

// Attach JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY)
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Inject the impersonation header when active. GET /auth/me is the one call
// that skips it: it decides who is signed in, and that stays the real admin.
// A WRITE to /auth/me is a profile edit and must land on whoever the admin is
// viewing as. Skipping writes too sent every View-as profile edit (focus areas,
// bio, the talent switches) to the admin's own account.
api.interceptors.request.use((config) => {
  if (config.url === '/auth/me' && (config.method ?? 'get').toLowerCase() === 'get') return config
  try {
    const stored = localStorage.getItem(IMPERSONATE_STORAGE_KEY)
    if (stored) {
      const target = JSON.parse(stored)
      if (target?.id) {
        config.headers['X-Impersonate-User'] = String(target.id)
      }
    }
  } catch { /* ignore parse errors */ }
  return config
})

// If the backend tells us the impersonation target no longer exists,
// drop the stale target and hard-reload so every cached query reruns
// without the bad X-Impersonate-User header. Without this, a deleted
// target leaves the app stuck rendering 404s forever.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail
    if (
      error.response?.status === 404 &&
      typeof detail === 'string' &&
      detail.startsWith('Impersonation target user ') &&
      detail.endsWith(' not found')
    ) {
      try {
        localStorage.removeItem(IMPERSONATE_STORAGE_KEY)
      } catch { /* ignore */ }
      window.location.reload()
    }
    return Promise.reject(error)
  }
)

// Handle 401 — redirect to login (clear both new + legacy token keys to prevent migration loop)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      localStorage.removeItem('token')
      // Only hard-redirect if not already on login page (prevents reload loop).
      // Carry the current address, so signing in again lands back here: a
      // full reload drops router state, and /ai-connect is useless without
      // its query (Copilot on #1554). Login runs it through safeRedirect.
      if (!window.location.pathname.endsWith('/login')) {
        const from = `${window.location.pathname}${window.location.search}`
        window.location.href = from === '/' ? '/login' : `/login?from=${encodeURIComponent(from)}`
      }
    }
    return Promise.reject(error)
  }
)

// Surface "emails sent" feedback: the backend tags responses whose action
// queued notification emails with an X-Emails-Sent header. Broadcast it as a
// window event so the toast listener (in ToastProvider) can tell the actor.
api.interceptors.response.use((response) => {
  // A posting says what it actually did — including the two outcomes where
  // nobody heard about the demand, which used to arrive as a plain 200.
  const outcome = response.headers?.['x-broadcast-outcome']
  if (outcome) {
    window.dispatchEvent(
      new CustomEvent('daccelerate:broadcast-outcome', { detail: { outcome: String(outcome) } }),
    )
    // Supersedes the generic count: X-Emails-Sent totals every mail the request
    // queued, confirmations included, so it reads a few higher than the number
    // of talents the dialog just promised. Two toasts disagreeing about the
    // same action is worse than one precise one.
    return response
  }
  const raw = response.headers?.['x-emails-sent']
  const count = raw ? parseInt(String(raw), 10) : 0
  if (count > 0) {
    window.dispatchEvent(new CustomEvent('daccelerate:emails-sent', { detail: { count } }))
  }
  return response
})

export default api

/**
 * The cross-focus-area warning (#995) — confirmed once, here, for every route.
 *
 * Acting on a demand outside your own focus area is not refused any more; it
 * answers 409 `cross_focus_area` and goes through when the request is re-sent
 * with `X-Acknowledge`. The backend raises it from a single gate shared by
 * twenty-three routes, so the client answers it in a single place too — a
 * per-call-site dialog would be twenty-three chances to forget one, and the
 * one forgotten would surface as a bare "Request failed with status code 409".
 *
 * `askToConfirm` is registered by CrossFocusAreaProvider. Until it is (in a
 * test, or before the tree mounts) the interceptor does nothing and the caller
 * sees the 409 unchanged — declining to guess on the user's behalf.
 *
 * The retry is safe because the refused request did not act: the gate runs
 * before any mutation, and `test_withdraw_invitation_from_another_fa_is_warned`
 * pins that the invitation is untouched after the 409.
 */
export const CROSS_FOCUS_AREA = 'cross_focus_area'

type ConfirmFn = (message: string) => Promise<boolean>

let askToConfirm: ConfirmFn | null = null

export function setCrossFocusAreaConfirm(fn: ConfirmFn | null) {
  askToConfirm = fn
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const detail = error.response?.data?.detail
    const isCrossFa =
      error.response?.status === 409 &&
      detail &&
      typeof detail === 'object' &&
      detail.code === CROSS_FOCUS_AREA
    if (!isCrossFa || !askToConfirm || error.config?.headers?.['X-Acknowledge']) {
      return Promise.reject(error)
    }
    const confirmed = await askToConfirm(String(detail.message ?? ''))
    if (!confirmed) return Promise.reject(error)
    return api.request({
      ...error.config,
      headers: { ...error.config.headers, 'X-Acknowledge': CROSS_FOCUS_AREA },
    })
  },
)
